import React, { useState, useEffect } from "react";
import { message } from "antd";
import PhoneInput from "react-phone-input-2";
import { useNavigate } from "react-router-dom";
import { auth, setDoc, db, doc, getDoc } from "../../firebase";
import { query, where, collection, getDocs } from "firebase/firestore";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import "./Register.css"; // Custom CSS for Government Style

const Register = () => {
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [formData, setFormData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    username: "",
    reUsername: "",
    password: "",
    rePassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showRePassword, setShowRePassword] = useState(false);
  const [usernameExists, setUsernameExists] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(
        auth,
        "recaptcha-container",
        {}
      );
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const sentOtp = async () => {
    if (!phone || phone.length < 10) {
      message.error("Enter a valid mobile number");
      return;
    }

    const formattedPhone = phone.startsWith("+") ? phone : `+${phone}`;

    if (!/^\+[1-9]\d{1,14}$/.test(formattedPhone)) {
      message.error("Invalid phone number format.");
      return;
    }

    try {
      const appVerifier = window.recaptchaVerifier;

      await signInWithPhoneNumber(auth, formattedPhone, appVerifier)
        .then((confirmationResult) => {
          setConfirmationResult(confirmationResult);
          message.success("OTP sent successfully");
        })
        .catch((error) => {
          console.error("Error during OTP send: ", error);
          message.error("Error sending OTP");
        });
    } catch (error) {
      console.log(error);
      message.error("Enter your mobile number");
    }
  };

  const checkIfPhoneExists = async (phone) => {
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("phone", "==", phone));
    const querySnapshot = await getDocs(q);

    return !querySnapshot.empty;
  };

  const verifyOtp = async () => {
    if (!otp) {
      message.error("Please enter the OTP");
      return;
    }

    if (confirmationResult) {
      confirmationResult
        .confirm(otp)
        .then(async (result) => {
          message.success("OTP verified successfully");

          const phoneExists = await checkIfPhoneExists(phone);

          if (phoneExists) {
            message.error("This phone number is already registered.");
            return;
          }

          localStorage.setItem("userPhone", phone);
          localStorage.setItem("userInfo", JSON.stringify(formData));
          setStep(2);
        })
        .catch((error) => {
          console.error("Error during OTP verification: ", error);
          message.error("Error verifying OTP");
        });
    } else {
      message.error("Please request an OTP first");
    }
  };

  const handleUsernameBlur = async (e) => {
    const username = e.target.value.trim();
    if (username) {
      const userDoc = doc(db, "users", username);
      const docSnap = await getDoc(userDoc);
      if (docSnap.exists()) {
        setUsernameExists(true);
        message.error("Username already exists");
      } else {
        setUsernameExists(false);
      }
    }
  };

  const handleSubmit = async () => {
    if (formData.password !== formData.rePassword) {
      message.error("Passwords do not match");
      return;
    }

    if (formData.username !== formData.reUsername) {
      message.error("Usernames do not match");
      return;
    }
    setLoading(true);
    try {
      const phoneExists = await checkIfPhoneExists(phone);

      if (phoneExists) {
        message.error("This phone number is already registered.");
        return;
      }

      const userDoc = doc(db, "users", formData.username);
      await setDoc(userDoc, {
        ...formData,
        phone,
      });

      message.success("Registration successful");
      localStorage.setItem("username", formData.username);
      localStorage.setItem("password", formData.password);

      navigate("/login");
    } catch (err) {
      console.error("Error during registration: ", err);
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleClickShowRePassword = () => setShowRePassword((show) => !show);

  return (
    <div className="register-container">
      <div className="register-form">
        {step === 1 && (
          <form>
            <div className="form-header">
              <h3>REGISTRATION</h3>
            </div>
            <input
              type="text"
              name="firstName"
              placeholder="First Name"
              value={formData.firstName}
              onChange={handleChange}
              required
              className="custom-textfield2"
            />
            <input
              type="text"
              name="middleName"
              placeholder="Middle Name"
              value={formData.middleName}
              onChange={handleChange}
              className="custom-textfield2"
            />
            <input
              type="text"
              name="lastName"
              placeholder="Last Name"
              value={formData.lastName}
              onChange={handleChange}
              required
              className="custom-textfield2"
            />
            <div id="recaptcha-container"></div>

            <PhoneInput country={"in"} value={phone} onChange={setPhone} className='phone-input' />
            <br />
            <button onClick={sentOtp} disabled={confirmationResult} className="send-btn">
              Send OTP
            </button>
            {confirmationResult && (
              <div>
                <input
                  type="text"
                  placeholder="Enter OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  required
                  className="custom-textfield"

                />
                <button onClick={verifyOtp}>Verify OTP</button>
              </div>
            )}
          </form>
        )}
        {step === 2 && (
          <form>
            <div className="form-header">
              <h3>Registration</h3>
            </div>
            <input
              type="text"
              name="username"
              placeholder="Create Username"
              value={formData.username}
              onChange={handleChange}
              onBlur={handleUsernameBlur}
              required
              className="custom-textfield"
            />
            <input
              type="text"
              name="reUsername"
              placeholder="Re-enter Username"
              value={formData.reUsername}
              onChange={handleChange}
              required
              className="custom-textfield"
            />
            <div>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Create Password"
                value={formData.password}
                onChange={handleChange}
                required
                className="custom-textfield"
              />
              <button type="button" onClick={handleClickShowPassword}>
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </button>
            </div>
            <div>
              <input
                type={showRePassword ? "text" : "password"}
                name="rePassword"
                placeholder="Re-enter Password"
                value={formData.rePassword}
                onChange={handleChange}
                required
                className="custom-textfield"
              />
              <button type="button" onClick={handleClickShowRePassword}>
                {showRePassword ? <VisibilityOff /> : <Visibility />}
              </button>
            </div>
            <button onClick={handleSubmit}>
              {loading ? "Loading..." : "Register"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Register;
